import { useCallback } from 'react';

/**
 * Boundary helpers for contenteditable or input elements
 */
const isAtStart = el => {
   if (!el) return true;
   if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
      return el.selectionStart === 0 && el.selectionEnd === 0;
   }
   const sel = window.getSelection();
   if (!sel.rangeCount) return true;
   const range = sel.getRangeAt(0);
   if (!range.collapsed) return false;

   const preRange = range.cloneRange();
   preRange.selectNodeContents(el);
   preRange.setEnd(range.startContainer, range.startOffset);
   return preRange.toString().length === 0;
};

const isAtEnd = el => {
   if (!el) return true;
   if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
      return el.selectionStart === el.value.length && el.selectionEnd === el.value.length;
   }
   const sel = window.getSelection();
   if (!sel.rangeCount) return true;
   const range = sel.getRangeAt(0);
   if (!range.collapsed) return false;

   const postRange = range.cloneRange();
   postRange.selectNodeContents(el);
   postRange.setStart(range.endContainer, range.endOffset);
   return postRange.toString().length === 0;
};

/**
 * useBehavioralKeyboard
 *
 * A high-level abstraction to make keyboard coordination easy for developers.
 *
 * @param {Object} ref - React ref to the input element
 * @param {Object} behaviors - Configuration for keyboard behaviors
 * @param {Function} behaviors.onEnter - Called on Enter (without shift).
 * @param {Function} behaviors.onEscape - Called on Escape.
 * @param {Function} behaviors.onArrowUp - Called on ArrowUp.
 * @param {Function} behaviors.onArrowDown - Called on ArrowDown.
 * @param {boolean} behaviors.edgeAware - If true, ArrowLeft/Right only stop propagation if NOT at boundaries.
 */
export const useBehavioralKeyboard = (ref, behaviors = {}) => {
   const { onEnter, onEscape, onArrowUp, onArrowDown, onArrowLeft, onArrowRight, edgeAware = false } = behaviors;

   const handleKeyDown = useCallback(
      e => {
         const el = ref.current;
         if (!el) return;

         // Helper to safely execute a callback and stop propagation
         const run = (cb, prevent = true) => {
            if (!cb) return false;
            const result = cb(e);
            if (result !== false) {
               if (prevent) e.preventDefault();
               e.stopPropagation();
            }
            return result;
         };

         // 1. Edge-Aware Navigation (Physical Movement)
         if (edgeAware) {
            if (e.key === 'ArrowLeft' && !isAtStart(el)) {
               return run(onArrowLeft || (() => {}), false);
            }
            if (e.key === 'ArrowRight' && !isAtEnd(el)) {
               return run(onArrowRight || (() => {}), false);
            }
         }

         // 2. Functional Keys (Standard Actions)
         switch (e.key) {
            case 'ArrowUp':
               return run(onArrowUp);
            case 'ArrowDown':
               return run(onArrowDown);
            case 'ArrowLeft':
               return !edgeAware && run(onArrowLeft, false);
            case 'ArrowRight':
               return !edgeAware && run(onArrowRight, false);
            case 'Enter':
               if (!e.shiftKey) return run(onEnter);
               break;
            case 'Esc':
            case 'Escape':
               return run(onEscape, false);
         }
      },
      [ref, onEnter, onEscape, onArrowUp, onArrowDown, onArrowLeft, onArrowRight, edgeAware],
   );

   return { handleKeyDown };
};

export default useBehavioralKeyboard;
